/**
 * useVerifyEmail Hook
 *
 * React Query mutation for email verification and admin user creation.
 */

import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { tenantService } from '../../../api/tenant.service';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { TenantSanitized } from '../../../types/tenant.types';
import type { UserSanitized } from '../../../types/user.types';

interface VerifyEmailRequest {
  verificationToken: string;
  firstName: string;
  lastName: string;
  password: string;
}

interface VerifyEmailResponse {
  message: string;
  data: {
    tenant: TenantSanitized;
    user: UserSanitized;
  };
}

interface UseVerifyEmailOptions {
  onSuccess?: (data: VerifyEmailResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for verifying email with token and creating admin user
 */
export const useVerifyEmail = (
  options?: UseVerifyEmailOptions
): UseMutationResult<VerifyEmailResponse, Error, VerifyEmailRequest> => {
  return useMutation<VerifyEmailResponse, Error, VerifyEmailRequest>({
    mutationFn: async (data: VerifyEmailRequest) => {
      // Call backend with enhanced verification request
      return await tenantService.verifyTenant(data);
    },
    onSuccess: (data) => {
      console.log('[useVerifyEmail] Email verified and admin user created successfully');
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useVerifyEmail] Verification failed:', getErrorMessage(error));
      options?.onError?.(error);
    },
  });
};
